import { useContext, useEffect, useState } from 'react';
import { DeviceSource } from '~/constants/common/DeviceSource';
import DeviceContext from '~/context/common/DeviceContext';

export default function useResizeListener({
  mobile,
  desktop
}: Record<'mobile' | 'desktop', number>) {
  const { setDeviceType, setScreenSize } = useContext(DeviceContext);

  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    let resizeTimeout: number;

    function onResize() {
      if (resizeTimeout) {
        window.cancelAnimationFrame(resizeTimeout);
      }

      resizeTimeout = window.requestAnimationFrame(() => {
        setHeight(
          Math.min(document.documentElement.clientHeight, window.innerHeight)
        );
        setWidth(
          Math.min(document.documentElement.clientWidth, window.innerWidth)
        );
      });
    }

    onResize();

    window.addEventListener('resize', onResize, false);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    const mediaQueries = {
      PORTRAIT_MEDIA_QUERY: '(orientation: portrait)',
      MOBILE_MEDIA_QUERY: `(max-width: ${mobile - 1}px)`,
      MOBILE_MEDIA_QUERY_WITH_ORIENTATION: `((orientation: portrait) and (max-width: ${
        mobile - 1
      }px)) or ((orientation: landscape) and (max-height: ${mobile - 1}px))`,
      TABLET_MEDIA_QUERY: `(min-width: ${mobile}px) and (max-width: ${
        desktop - 1
      }px)`,
      DESKTOP_MEDIA_QUERY: `(min-width: ${desktop}px)`
    };

    const { matchMedia } = window;
    const isPortrait = matchMedia(mediaQueries.PORTRAIT_MEDIA_QUERY).matches;
    const isLandscape = !isPortrait;
    const isMobile = matchMedia(mediaQueries.MOBILE_MEDIA_QUERY).matches;
    const isMobileWithOrientation = matchMedia(
      mediaQueries.MOBILE_MEDIA_QUERY_WITH_ORIENTATION
    ).matches;
    const isTablet = matchMedia(mediaQueries.TABLET_MEDIA_QUERY).matches;
    const isDesktop = matchMedia(mediaQueries.DESKTOP_MEDIA_QUERY).matches;

    setDeviceType({
      devicePixelRatio: window.devicePixelRatio,
      isDesktop,
      isLandscape,
      isMobile,
      isMobileWithOrientation,
      isPortrait,
      isTablet,
      source: DeviceSource.Browser
    });

    setScreenSize({ height, width });
  }, [desktop, height, mobile, setDeviceType, setScreenSize, width]);
}
